import { resolveMeterColor, useTranslation } from "@next-platform/client-common-core";
import {
  DefinitionLevelList,
  formatPublicLevelRange,
  meterTypeSummary,
  publicLevelSegmentWeights,
} from "@next-platform/client-common-core/rulebook/profile/meterDefinitionRows";
import { Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import type { PublicMeterLevel, PublicMeterView } from "@next-platform/packages-ts-types";
import { MeterType } from "@next-platform/packages-ts-types";
import { memo } from "react";

import { Col } from "@/components";
import RulebookLevelSegments from "@/components/Rulebook/RulebookLevelSegments";
import type { RenderTokenChip } from "@/components/Rulebook/tokenChipTint";
import Trans from "@/components/Trans/Trans";
import { useMeterColorTheme } from "@/hooks";

import RulebookInstructionsPrivateNotice from "./RulebookInstructionsPrivateNotice";
import RulebookProfileMeterCardHeader from "./RulebookProfileMeterCardHeader";
import RulebookProfileMeterLevelRow from "./RulebookProfileMeterLevelRow";
import RulebookProfileSectionHeading from "./RulebookProfileSectionHeading";
import useProfileTokenRenderer from "./useProfileTokenRenderer";

type Translate = (key: string, values?: Record<string, unknown>) => string;

type PublicMeterLevelList = {
  levels: PublicMeterLevel[];
  kind: DefinitionLevelList;
  suffix?: string;
};

const isRevealedLevel = (level: PublicMeterLevel): level is PublicMeterLevel & { label: string } =>
  level.revealed && typeof level.label === "string" && level.label.length > 0;

const getPublicMeterLevelLists = (meter: PublicMeterView): PublicMeterLevelList[] => {
  if (meter.type === MeterType.CLOCK) {
    return [
      { levels: meter.tod_levels ?? [], kind: DefinitionLevelList.TOD, suffix: "tod" },
      { levels: meter.day_levels ?? [], kind: DefinitionLevelList.DAY, suffix: "day" },
    ];
  }
  return [{ levels: meter.levels, kind: DefinitionLevelList.VALUE }];
};

type RulebookMeterLevelTableProps = {
  meter: PublicMeterView;
  levels: PublicMeterLevel[];
  kind: DefinitionLevelList;
  color?: string;
  suffix?: string;
  translate: Translate;
  renderChip: RenderTokenChip;
};

const RulebookMeterLevelTable = ({ meter, levels, kind, color, suffix, translate, renderChip }: RulebookMeterLevelTableProps) => {
  if (!levels.length) {
    return null;
  }
  const segments = publicLevelSegmentWeights({ meter, levels, kind });
  const segmentTestId = `RulebookProfile-LevelSegments-${meter.id}${suffix ? `-${suffix}` : ""}`;
  const revealedLevels = levels.filter(isRevealedLevel);

  return (
    <div
      className="flex flex-col overflow-hidden rounded-[10px] border border-solid border-gray-6 dark:border-gray-8"
      data-testid={`RulebookProfile-Meter-${meter.id}-LevelList${suffix ? `-${suffix}` : ""}`}
    >
      <RulebookLevelSegments segments={segments} color={color} data-testid={segmentTestId} />
      {revealedLevels.map((level, index) => (
        <RulebookProfileMeterLevelRow
          key={`${level.from}-${index}`}
          level={level}
          range={formatPublicLevelRange({ level, kind, translate })}
          renderChip={renderChip}
          data-testid={`RulebookProfile-Meter-${meter.id}-Level-${index}`}
        />
      ))}
    </div>
  );
};

type RulebookMeterRowProps = {
  meter: PublicMeterView;
  renderChip: RenderTokenChip;
};

const RulebookMeterRow = ({ meter, renderChip }: RulebookMeterRowProps) => {
  const { t } = useTranslation();
  const theme = useMeterColorTheme();
  const iconColor = resolveMeterColor({ color: meter.color, theme });
  const levelLists = getPublicMeterLevelLists(meter);
  const typeLine = meterTypeSummary(meter, t);

  return (
    <article
      className="flex flex-col gap-3 rounded-[14px] border border-solid border-gray-5 dark:border-gray-8 bg-gray-2 p-4"
      data-testid={`RulebookProfile-Meter-${meter.id}`}
    >
      <RulebookProfileMeterCardHeader
        icon={meter.icon}
        meterType={meter.type}
        color={iconColor}
        label={meter.label ?? meter.id}
        typeLine={typeLine}
        isShown={true}
      />
      {levelLists.map((levelList) => (
        <RulebookMeterLevelTable
          key={levelList.suffix ?? "value"}
          meter={meter}
          color={iconColor}
          translate={t}
          renderChip={renderChip}
          {...levelList}
        />
      ))}
    </article>
  );
};

type RulebookMetersSectionProps = {
  meters: PublicMeterView[];
  hasHiddenMeters: boolean;
};

/** The rulebook's public meters — shown bounds and labels, without private instructions or drivers. */
const RulebookMetersSection = ({ meters, hasHiddenMeters }: RulebookMetersSectionProps) => {
  const renderChip = useProfileTokenRenderer({ publicMeters: meters });
  return (
    <Col className="gap-3" data-testid="RulebookProfile-Meters">
      <RulebookProfileSectionHeading
        title={<Trans i18nKey="rulebook:profile.meters" />}
        subtitle={<Trans i18nKey="rulebook:profile.metersSubtitlePrivate" />}
      />
      <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2">
        {meters.map((meter) => (
          <RulebookMeterRow key={meter.id} meter={meter} renderChip={renderChip} />
        ))}
      </div>
      {hasHiddenMeters ? (
        <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11" data-testid="RulebookProfile-HiddenMetersNote">
          <Trans i18nKey="rulebook:profile.hiddenMetersNote" />
        </Text>
      ) : null}
      <RulebookInstructionsPrivateNotice />
    </Col>
  );
};

export default memo(RulebookMetersSection);
